// SideContactBadge.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { globalUiTranslations } from '../i18n/translations';

const MAUVE = '#4c2f8e';
const BADGE_INLINE = 14;   // épaisseur du badge (px) – discret
const BADGE_BLOCK  = 120;  // hauteur du badge (px)
const PANEL_WIDTH  = 300;  // largeur du panneau ouvert

export default function SideContactBadge({
  top,
  logoSelector = '#site-logo',
  whatsappHref = 'https://wa.me/32471241232',
  whatsappLabel = '+32 471 24 12 32',
  email = 'mendyxevents@gmail.com',
}) {
  const { language } = useLanguage();
  const t = createTranslator(language, globalUiTranslations);
  const [open, setOpen] = useState(false);
  const [computedTop, setComputedTop] = useState(top ?? 320);

  // Safe-area iOS (neutre ailleurs)
  const paddingRightSafeArea = useMemo(() => {
    if (typeof window === 'undefined' || !('CSS' in window)) return 0;
    try {
      return window.CSS.supports('padding-right: env(safe-area-inset-right)')
        ? 'env(safe-area-inset-right)'
        : 0;
    } catch {
      return 0;
    }
  }, []);

  // Auto-alignement vertical sur le logo si `top` non fourni
  useEffect(() => {
    if (top != null) return;

    const el = typeof document !== 'undefined' ? document.querySelector(logoSelector) : null;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect?.();
      if (!rect) return;
      const y = Math.max(16, Math.round(rect.top + rect.height / 2 - BADGE_BLOCK / 2));
      setComputedTop(y);
    };

    // Mesures échelonnées pour couvrir fonts/images/viewport mobile
    compute();
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(compute));
    const t1 = setTimeout(compute, 120);
    const t2 = setTimeout(compute, 400);
    const t3 = setTimeout(compute, 800);

    const onResizeLike = () => compute();
    window.addEventListener('load', onResizeLike, { passive: true });
    window.addEventListener('orientationchange', onResizeLike, { passive: true });
    window.addEventListener('resize', onResizeLike, { passive: true });
    window.visualViewport?.addEventListener('resize', onResizeLike, { passive: true });

    let ro;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(compute);
      try { ro.observe(el); } catch {}
    }

    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      window.removeEventListener('load', onResizeLike);
      window.removeEventListener('orientationchange', onResizeLike);
      window.removeEventListener('resize', onResizeLike);
      window.visualViewport?.removeEventListener('resize', onResizeLike);
      ro?.disconnect();
    };
  }, [top, logoSelector]);

  return (
    <div
      style={{
        position: 'fixed',
        top: `${computedTop}px`,
        right: 0,
        paddingRight: paddingRightSafeArea, // évite le chevauchement sur iPhone
        zIndex: 1000,
        fontFamily: 'sans-serif',
        // ancrage pixel-perfect mobile
        transform: 'translateZ(0)',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Boîte du badge uniquement (largeur = BADGE_INLINE) */}
      <div
        style={{
          position: 'relative',
          width: BADGE_INLINE,
          height: BADGE_BLOCK,
        }}
      >
        {/* Panneau coulissant (n'affecte pas la largeur → jamais de décalage) */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            right: BADGE_INLINE,                  // se place à gauche du badge
            width: PANEL_WIDTH,
            transform: open ? 'translate(0, -50%)' : 'translate(100%, -50%)',
            transition: 'transform .25s ease',
            background: MAUVE,
            color: '#fff',
            padding: '12px 20px',
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            boxShadow: open ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
            pointerEvents: open ? 'auto' : 'none',
            overflow: 'hidden',
            willChange: 'transform',
          }}
          aria-hidden={!open}
          role="region"
          aria-label={t('contactDetails')}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {t('contactQuestion')}
          </div>
          <div style={{ lineHeight: 1.5 }}>
            💬 WhatsApp :{' '}
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#fff', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {whatsappLabel}
            </a>
            <br />
            ✉️ Email :{' '}
            <a
              href={`mailto:${email}`}
              style={{ color: '#fff', fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {email}
            </a>
          </div>
        </div>

        {/* Badge vertical ultra fin (identique au comportement d’origine) */}
        <button
          onClick={() => setOpen(v => !v)}
          title={t('contactTitle')}
          aria-expanded={open}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: BADGE_INLINE,
            height: BADGE_BLOCK,
            backgroundColor: MAUVE,
            color: '#fff',
            border: 0,
            cursor: 'pointer',
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            userSelect: 'none',
            overflow: 'hidden',          // évite tout débordement qui pourrait “pousser”
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontWeight: 700,
              fontSize: '0.9rem',
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {open ? '✖' : t('contact')}
          </span>
        </button>
      </div>
    </div>
  );
}
